import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiCalendar,
  FiClock,
  FiEye,
  FiFileText,
  FiSearch,
} from 'react-icons/fi';
import EmptyState from '../components/EmptyState';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { richTextToPlainText } from '../utils/richText';
import './Articles.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const normalizeArticle = (article, formatDate) => {
  const id = article._id || article.id;
  const rawImageUrl = article.imageUrl || '';
  const imageUrl = rawImageUrl.startsWith('/') ? `${API_URL}${rawImageUrl}` : rawImageUrl;
  const dateValue = article.createdAt || article.date;
  const date = dateValue
    ? formatDate(dateValue, { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const wordCount = richTextToPlainText(article.content || article.excerpt).split(/\s+/).filter(Boolean).length;
  const readingMinutes = Math.max(1, Math.ceil(wordCount / 220));
  return { ...article, id, imageUrl, date, readingMinutes };
};

const Articles = () => {
  const { t } = useTranslation();
  const { locale, localePath, formatDate, formatNumber } = useLocale();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('__all__');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await api.get(`/api/articles?locale=${encodeURIComponent(locale.code)}`);
        if (res.success && Array.isArray(res.data)) {
          setArticles(res.data.map((article) => normalizeArticle(article, formatDate)));
        }
      } catch (_) {
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [formatDate, locale.code]);

  const categories = useMemo(
    () => ['__all__', ...new Set(articles.map((article) => article.category).filter(Boolean))],
    [articles]
  );

  const filteredArticles = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    return articles.filter((article) => {
      const matchesCategory = selectedCategory === '__all__' || article.category === selectedCategory;
      const matchesSearch = !normalizedSearch
        || article.title.toLowerCase().includes(normalizedSearch)
        || String(article.excerpt || '').toLowerCase().includes(normalizedSearch)
        || String(article.category || '').toLowerCase().includes(normalizedSearch);
      return matchesCategory && matchesSearch;
    });
  }, [articles, searchQuery, selectedCategory]);

  const resetFilters = () => {
    setSelectedCategory('__all__');
    setSearchQuery('');
  };

  return (
    <div className="articles-page">
      <section className="articles-hero">
        <div className="articles-container">
          <span className="articles-eyebrow"><FiFileText aria-hidden="true" /> {t('articles.magazine')}</span>
          <h1>{t('articles.title')}</h1>
          <p>{t('articles.subtitle')}</p>
          <div className="articles-search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t('articles.searchPlaceholder')}
              aria-label={t('articles.searchPlaceholder')}
            />
          </div>
        </div>
      </section>

      <main className="articles-container articles-main">
        {!loading && articles.length > 0 && (
          <div className="articles-toolbar">
            <div className="articles-filters" role="group" aria-label={t('articles.filtersLabel')}>
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  className={`articles-filter-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === '__all__' ? t('articles.all') : category}
                </button>
              ))}
            </div>
            <span className="articles-result-count">
              {t('articles.count', { count: formatNumber(filteredArticles.length) })}
            </span>
          </div>
        )}

        {loading ? (
          <div className="articles-loading">{t('articles.loading')}</div>
        ) : articles.length === 0 ? (
          <EmptyState name={t('articles.name')} icon={<FiFileText />} />
        ) : filteredArticles.length === 0 ? (
          <div className="articles-empty-filter">
            <FiSearch aria-hidden="true" />
            <h2>{t('articles.empty')}</h2>
            <p>{t('articles.emptyHint')}</p>
            <button type="button" onClick={resetFilters}>{t('articles.clear')}</button>
          </div>
        ) : (
          <div className="articles-grid">
            {filteredArticles.map((article, index) => (
              <Link
                key={article.id}
                to={localePath(`/articles/${article.slug || article.id}`)}
                className="article-card"
                style={{ animationDelay: `${Math.min(index, 6) * 70}ms` }}
                aria-label={t('articles.readLabel', { title: article.title })}
              >
                <div className="article-card-image">
                  {article.imageUrl ? (
                    <img src={article.imageUrl} alt={article.imageAlt || article.title} loading="lazy" />
                  ) : (
                    <div className="article-card-placeholder"><FiFileText aria-hidden="true" /></div>
                  )}
                  <span className="article-card-category">{article.category}</span>
                </div>

                <div className="article-card-content">
                  <div className="article-card-meta">
                    <span><FiCalendar aria-hidden="true" /> {article.date}</span>
                    <span><FiClock aria-hidden="true" /> {t('articles.readingTime', { count: formatNumber(article.readingMinutes) })}</span>
                  </div>
                  <h2>{article.title}</h2>
                  <p className="article-card-excerpt">{article.excerpt}</p>
                  <footer className="article-card-footer">
                    <span className="article-card-views"><FiEye aria-hidden="true" /> {t('articles.views', { count: formatNumber(article.views || 0) })}</span>
                    <span className="article-card-read-more">{t('articles.read')} <FiArrowLeft aria-hidden="true" /></span>
                  </footer>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Articles;
